<template>
  <v-col cols="12">
    <h3 class="text-h4 font-weight-bold mb-4 mt-0">API ROBisu</h3>
    <p class="mb-4">
      API slouží k získání dat z databáze nebo naopak poslání dat do databáze robisu. Využívá se standardizovaných formátů dat. Pro JSON formát vycházíme z nově vytvořeného <a href="https://github.com/AROB-CR/radio-o-standards/blob/main/ardf-json/ardf_schema.json" target="_blank" class="text-primary">ARDF JSON standardu</a>.
      Pro nahrání výsledků softwary třetích stran (vyčítací softwary) je zapotřebí poslat spolu s daty i <b>Race-Api-Key</b> (nění-li uvedeno jinak) v hlavičce požadavku (ne v url adrese). Tento klíč najdete na stránce nastavení etapy. Má tvar např. <code>4bc837b344e642dd86af1b04bc4313ae</code> Pokud se dotazujete na celý event (soutěž), můžete poslat klíč z jakékoli etapy v soutěži.
    </p>
    <v-expansion-panels variant="accordion">
      <!-- GET JSON event -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-blue-accent-3">GET</span> <code class="font-weight-bold">api/?type=json&name=event&event_id=<span class="text-red">robis ID soutěže</span></code> Vrací data z celé soutěže (soutěž -> etapy -> přihlášky -> startovky -> výsledky)
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Pro získání dat je nutné přihlášení v prohlížeči a posílat v parametru event_id, nebo při GET požadavku poslat <b>Race-Api-Key</b> v hlavičce a tím pádem event_id již není nutný. API klíč můžete posílat z jakékoli etapy v soutěži. Má tvar např. <code>4bc837b344e642dd86af1b04bc4313ae</code>
          </p>
          <code class="text-blue my-3">
            https://rob-is.cz/api/?type=json&name=event&event_id=<span class="text-red">robis ID soutěže</span>
          </code>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- GET JSON race -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-blue-accent-3">GET</span> <code class="font-weight-bold">api/?type=json&name=race&race_id=<span class="text-red">robis ID etapy</span></code> Vrací data z jedné etapy(etapa -> přihlášky -> startovky -> výsledky)
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Pro získání dat je nutné přihlášení v prohlížeči a posílat v parametru event_id, nebo při GET požadavku poslat <b>Race-Api-Key</b> v hlavičce a tím pádem race_id již není nutný. Api klíč najdete v nastavení etapy. Má tvar např. <code>4bc837b344e642dd86af1b04bc4313ae</code>
          </p>
          <code class="text-blue my-3">
            https://rob-is.cz/api/?type=json&name=race&race_id=<span class="text-red">robis ID etapy</span>
          </code>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- GET JSON accounts list with properties -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-blue-accent-3">GET</span> <code class="font-weight-bold">api/account/?name=<span class="text-red">*</span>&index=<span class="text-red">*</span>&si_number=<span class="text-red">*</span>&value=<span class="text-red">*</span></code> Vrátí seznam účtů na základě shody s některým z parametrů
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Místo <span class="text-red">*</span> posílejte písmena nebo čísla. Je nutné odeslat alespoň nekterý z parametrů, aby endpoint vrátil výsledek (i prázdný). Lze posílat pouze jeden z možných parametrů. <b>Name</b> hledá jak ve jméně, tak v příjmení. <b>Value</b> vyhledá ze všech parametrů naráz a vrací všechny shody. Pokud se posílá value, ostatní parametry se ignorují. <b>Není potřeba</b> posílat Race-Api-Key.
          </p>
          <code class="text-blue my-3">
            https://rob-is.cz/api/account/?name=<span class="text-red">*</span>&index=<span class="text-red">*</span>&si_number=<span class="text-red">*</span>&value=<span class="text-red">*</span>
          </code>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- GET JSON O-checklist data -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-blue-accent-3">GET</span> <code class="font-weight-bold">api/ochecklist/</code> Vrací data z aplikace O-checklist
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Pozor, endpoint očekává v hlavičce <b>Key</b> a jeho hodnotu. Přijatý <b>Key</b> vyfiltruje záznamy přijaté z O-checklist právě s tímto klíčem. Je potřeba, aby tento klíč organizátoři zadali jako parametr v aplikaci O-checklist. Jak to celé funguje se dozvíte <a href="#" data-topic="other-ochecklist">v nápovědě</a>.
          </p>
            <code class="text-blue my-3">
              https://rob-is.cz/api/ochecklist/
            </code>
          <p>
            <a :href="`${BaseUrl}static/GET_ochecklist.json`" target="_blank" class="text-primary">
              Příklad výstupu
            </a>
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PUT race data -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-orange-darken-2">PUT</span> <code class="font-weight-bold" @click.stop>api/race/</code> Přepíše data pro etapu.
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Od verze 1.0.6 robis z tohoto endpointu zpracovává všechny proměnné pro etapu a kategorie v etapě. Nová kategorie se vytvoří, pokud je typická podle pravidel, nebo pokud se s ní posílá category_option, což je speciální proměnná pro robis, aby podle kategorií mohl počítat žebříčky. Možnosti hodnot category_options jsou níže v odkazu.<br>
            Pokud proměnné neposíláte, nebo jsou prázdné, data se nepřepisují (kromě seznamu vyhledávaných kontrol, když se posílá prázdné pole). Response vrací "updated_race": [], "created_categories": [], "updated_categories": [], "invalid": []. Validní data se přepíší, nevalidní se vypíší v invalid.
          </p>
          <ul>
            <code class="text-blue my-3">
              https://rob-is.cz/api/race/
            </code>
          </ul>
          <p>
             <a :href="`${BaseUrl}static/PUT_race_example.json`" target="_blank" class="text-primary">
              Příklad dat pro PUT JSON race
            </a>,
             <a :href="`${BaseUrl}static/PUT_race_category_options.json`" target="_blank" class="text-primary">
              povolené hodnoty category_option
            </a>
          </p>
          <!-- <p>
            Statusy: 400 - není race api key nebo špatné kódování, obecná chyba, 404 - nesprávný race api key, 406 - nesprávná přípona souboru, 423 - Dnešní datum se neshoduje s datem závodu, 200 - OK, 207 - některá data nebyla uložena
          </p> -->

        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- POST JSON startlist -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-green">POST</span> <code class="font-weight-bold" @click.stop>api/startlist/?valid=True</code> Nahraje startovky pro etapu
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Smaže všechny existující startovní časy závodníků v etapě a uloží nové z posílaných dat. Pokud data v JSONu obsahují chyby, vrátí se tyto chyby v response a data se neuloží. V requestu lze posílat buď reálný čas dne, nebo relativní start od startu 00, nebo oba. Kůli např. nočním závodům, u kterých může docházet ke startům přes půlnoc je lepší posílat relativní start.
          </p>
          <code class="text-blue my-3">
            https://rob-is.cz/api/startlist/?valid=True
          </code>
          <p>
            <a :href="`${BaseUrl}static/POST_startlist.json`" target="_blank" class="text-primary">
              Formát pro POST JSON startlist
            </a>, 
            <a :href="`${BaseUrl}static/POST_startlist_example.json`" target="_blank" class="text-primary">
              příklad dat pro odeslání
            </a>
          </p>
          <p>
            Statusy: 406 - startovka nemůže být uložena. Podrobnosti jsou v response v error, 404 jiná neznámá chyba, 403 - chybný API klíč, nebo soutěž je již uzamčená, 201 - úspěch
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- POST JSON results -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-green">POST</span> <code class="font-weight-bold" @click.stop>api/results/?valid=True</code> Nahraje konečné výsledky pro etapu
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Nahrání (a přepsání) dat týkajících se výsledků v závodu. Všechny existující výsledky v robisu se smažou, pokud neexistují v posílaném JSONu! Pokud neodesíláte pro kategorii vyhledávané kontroly, v databázi se nepřepíší na prázdnoou hodnotu, ale zůstanou v původním stavu. Pokud data v JSONu obsahují chyby, vrátí se tyto chyby v response a data se neuloží. Při odpovědi 200 (OK) se do response vypíší také přihlášení závodníci, kterým jsi výsledek v JSONu neodesílal, ale jsou na etapu příhlášení.
          </p>
          <code class="text-blue my-3">
            https://rob-is.cz/api/results/?valid=True
          </code>
          <p>
            <a :href="`${BaseUrl}static/POST_results.json`" target="_blank" class="text-primary">
              Formát pro POST JSON final results
            </a>, 
            <a :href="`${BaseUrl}static/POST_results_example.json`" target="_blank" class="text-primary">
              příklad dat pro odeslání
            </a>
          </p>
          <p>
            Statusy: 400 - invalid race_id, 403 invalid race api key, 406 - invalid data  
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PUT CSV results -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-orange-darken-2">PUT</span> <code class="font-weight-bold" @click.stop>api/results/?name=csv</code> Nahraje průběžné výsledky z&nbsp; <b>CSV souboru</b>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
             Nahrávání online výsledků. Data přepisují pouze výsledek závodníka, který se v datech posílá a má rozdílné hodnoty oproti těm uloženým v robisu (došlo k nějaké změně). Ostatní výsledky v databázi zůstanou beze změny. Pokud závodník v etapě nebo soutěži není přihlášený, robis mu přihlášku vytvoří. Neposílejte závodníky, kteří už výsledek mají a nechcete jej v robisu přepisovat, ať zbytečně nezatěžuejete výpočetní výkon serveru. V response se vrátí, kdo byl založený (created) a komu se uložily nějaké změny (updated). Pokud jsou některá data nevalidní, response vrátí chyby pro tyto konkrétní závodníky. 

          </p>
          <ul>
            <code class="text-blue my-3">
              https://rob-is.cz/api/results/?name=csv
            </code>
          </ul>
          <p>
            <code>
              Index;SI number;Last name;First name;Category name;Run time;Place;Controls num.;Result status;Punches<br>
              Punches = Code/alias,Control Type,Punch status,Split time 
            </code><br>
            Jednotlivé Punches (mezičasy) jsou oddělené # Údeje v panches jsou oddělené , Hlavní údaje jsou oddělené ; Všechny hodnoty, které v proměnných mohou nabývat naleznete <a href="https://github.com/kolskypavel/Radio-O-Manager/wiki/Formaty_dat#csv-2" target="_blank">na githubu</a>. Control Type nabývá hodnot: CONTROL; BEACON; FINISH; SEPARATOR. Posílaná data jsou volitelná, ale kvůli nalezení shody musíš posílat podle priority: Index nebo Příjmení a Jméno nebo Číslo čipu (pouze číslo čipu doporučujeme posílat jen v případě mezičasu z radiokontroly). Robis očekává binární soubor.

          </p>
          <p>
            <a :href="`${BaseUrl}static/PUT_results.csv`" target="_blank" class="text-primary">
              Příklad dat pro PUT CSV online results
            </a>
          </p>
          <p>
            Statusy: 400 - není race api key nebo špatné kódování, obecná chyba, 404 - nesprávný race api key, 406 - nesprávná přípona souboru, 423 - Dnešní datum se neshoduje s datem závodu, 200 - OK, 207 - některá data nebyla uložena
          </p>

        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PUT JSON results -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-orange-darken-2">PUT</span> <code class="font-weight-bold" @click.stop>api/results/?name=json</code> Nahraje průběžné výsledky z&nbsp; <b>JSON</b>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Nahrávání online výsledků. Data přepisují pouze výsledek závodníka, který se v datech posílá a má rozdílné hodnoty oproti těm uloženým v robisu (došlo k nějaké změně). Ostatní výsledky v databázi zůstanou beze změny. Pokud závodník v etapě nebo soutěži není přihlášený, robis mu přihlášku vytvoří. Neposílejte závodníky, kteří už výsledek mají a nechcete jej v robisu přepisovat, ať zbytečně nezatěžuejete výpočetní výkon serveru. V response se vrátí, kdo byl založený (created) a komu se uložily nějaké změny (updated). Pokud jsou některá data nevalidní, response vrátí chyby pro tyto konkrétní závodníky. 
          </p>
          <ul>
            <code class="text-blue my-3">
              https://rob-is.cz/api/results/?name=json
            </code>
          </ul>
          <p>
            Více o formátu naleznete <a href="https://github.com/AROB-CR/radio-o-standards/tree/main/ardf-json" target="_blank">na githubu</a>. Posílaná data jsou volitelná, ale kvůli nalezení shody musíš posílat podle priority: Entry ID neboIndex nebo Příjmení a Jméno nebo Číslo čipu.
          </p>
          <p>
            <a :href="`${BaseUrl}static/PUT_results.json`" target="_blank" class="text-primary">
              Formát dat pro PUT JSON online results
            </a>,
              <a :href="`${BaseUrl}static/PUT_results_example.json`" target="_blank" class="text-primary">
              příklad dat pro odeslání
            </a>
          </p>
          <p>
            Statusy: 400 - není race api key nebo špatné kódování, obecná chyba, 404 - nesprávný race api key, 423 - Dnešní datum se neshoduje s datem závodu, 200 - OK, 207 - některá data nebyla uložena
          </p>

        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PUT JSON FjwW results -->
      <!-- <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-orange-darken-2">PUT</span> <code class="font-weight-bold" @click.stop>api/results/?name=json_fjww</code> Nahraje průběžné výsledky z&nbsp; <b>JSON</b>&nbsp;z programu FjwW
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Nahrávání online výsledků. Data přepisují pouze výsledek závodníka, který se v datech posílá a má rozdílné hodnoty oproti těm uloženým v robisu (došlo k nějaké změně). Ostatní výsledky v databázi zůstanou beze změny. Pokud závodník v etapě nebo soutěži není přihlášený, robis mu přihlášku vytvoří. Neposílejte závodníky, kteří už výsledek mají a nechcete jej v robisu přepisovat, ať zbytečně nezatěžuejete výpočetní výkon serveru. V response se vrátí, kdo byl založený (created) a komu se uložily nějaké změny (updated). Pokud jsou některá data nevalidní, response vrátí chyby pro tyto konkrétní závodníky. 
          </p>
          <ul>
            <code class="text-blue my-3">
              https://rob-is.cz/api/results/?name=json_fjww
            </code>
          </ul>
          <p>
            <a :href="`${BaseUrl}static/PUT_results_fjww_schema.json`" target="_blank" class="text-primary">
              Formát dat pro PUT JSON online results
            </a>,
              <a :href="`${BaseUrl}static/PUT_results_fjww_example.json`" target="_blank" class="text-primary">
              příklad dat pro odeslání
            </a>
          </p>
          <p>
            Statusy: 400 - není race api key nebo špatné kódování, obecná chyba, 404 - nesprávný race api key, 423 - Dnešní datum se neshoduje s datem závodu, 200 - OK, 207 - některá data nebyla uložena
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel> -->



      <!-- PUT RADIO results -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="method btn bg-green">POST</span> <code class="font-weight-bold" @click.stop>api/radio/</code> Nahraje mezičas oražení z&nbsp; <b>radiokontroly</b>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Tento endpoint je velmi ořezaný, protože slouží k přijímání dat z radiokontrol. Předpokládá se, že radiokontrola posílá pouze číslo čipu, kód kontroly a čas oražení ve formátu hh:mm:ss. Aby zobrazení nahraných mezičasů dobře fungovalo, je potřeba mít v závodě nahrané startovky, ze kterých ROBis mezičas dopočítá a uloží. Zobrazit mezičasy z radiokontrol lze na stránce <router-link to="/vysledky" target="_blank">Výsledky</router-link>.
          </p>
          <ul>
            <code class="text-blue my-3">
              https://rob-is.cz/api/radio/
            </code>
          </ul>

          <p>
            <a :href="`${BaseUrl}static/POST_radio_control.json`" target="_blank" class="text-primary">
              Struktura dat pro PUT RADIO online splittimes
            </a>,
              <a :href="`${BaseUrl}static/POST_radio_control_example.json`" target="_blank" class="text-primary">
              příklad dat pro odeslání
            </a>
          </p>
          <p>
            Statusy: 207 - některé mezičasy nebyly uložené do výsledků závodu, nebo závod nebyl nalezen (viz chybové hlášky v response) (i tak se data uloží do seznamu s možností je upravit a znovu odeslat), 200 - OK (data byla uložena na server)
          </p>

        </v-expansion-panel-text>
      </v-expansion-panel>

               




      <!-- PUT JSON results
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold">
          <span class="method btn bg-orange-darken-2">PUT</span> <span >JSON results (konečné)</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Nahrání (a přepsání) dat týkajících se výsledků v závodu. Všechny existující výsledky v robisu se smažou, pokud neexistují v
            posílaném JSONu! Vyžaduje <b>Event_Api_Key</b>.
          </p>
          <ul class="mb-3">
            <code class="text-blue">
              https://rob-is.cz/api/results/?valid=True&race_id=<span class="text-red">robis ID etapy</span>
            </code>
          </ul>
          <p>
            put text
          </p>
          <p>
            <a :href="`${BaseUrl}static/POST_results.json`" target="_blank" class="text-primary">
              Formát pro POST JSON final results
            </a>
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>-->
      <!-- DELETE JSON results -->
      <!-- <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold">
          <span class="method btn bg-deep-orange-accent-4">DELETE</span> <span >JSON results (konečné)</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            text
          </p>
          <p>
            <a :href="`${BaseUrl}static/POST_results.json`" target="_blank" class="text-primary">
              Formát pro POST JSON final results
            </a>
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel> --> 

    </v-expansion-panels>
  </v-col>



</template>

<script setup>
import { ref } from "vue";
const BaseUrl = ref(process.env.VUE_APP_API_STATIC_URL);
</script>

<style scoped>
.text-blue {
  color: #1e88e5;
}
.text-red {
  color: #e53935;
}
.method {
  font-weight: bold;
  padding: 8px 12px;
  border-radius: 4px;
  font-weight: bold;
  margin-right: 10px;
}


code {
  background-color: rgb(var(--v-theme-code));
  color: rgb(var(--v-theme-on-code));
  padding: 4px 8px;
  margin-right: 10px;
  border-radius: 4px;
  display: inline-block;
  word-break: break-all;
}
p {
  text-align: justify;
  line-height: 27px;
}
</style>
